import styles from './template.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import {storeInstance} from '@aofl/store/dist/legacy';
import {until} from 'lit-html/directives/until';
import {i18nMixin} from '@aofl/i18n-mixin';
import {routerInstance} from '@aofl/router';
import translations from '../../i18n';
import {sdoNs, modals, permissions} from '@modules/constants-enumerate';
import '@modules/sdo-licenses';
import {ApiService} from '@modules/api-service';
import {SearchParams} from '@modules/search-url-params';
import {defaults} from '@aofl/object-utils';
import './sdo';

const DEFAULT_SEARCH = {
  field: 'group_name',
  query: ''
};
const DEFAULT_SORT = {
  fields: ['group_name', 'group_id'],
  direction: 'asc',
  limit: 10,
  is_forward: true
};

const DEFAULT_QUERY_OBJ = {
  sort: {
    fields: ['name', 'id'],
    direction: 'asc',
    limit: 10,
    is_forward: true
  }
};

const setTranslations = (ctx) => {
  const t10s = {
    tableConfig: [
      {
        label: ctx.__('<tt-2WnNh9RX>', 'Name'),
        key: 'group_name',
        sortable: false,
        order: '',
        classList: 'text-nowrap col-2 align-items-center',
        headClassList: 'text-left col-2',
        render: (html, result, index) => html`<div class="text-left">${result.group_status === 'inactive' ? until(ctx._r(ctx.__('<tt-fhsknVIc>', '%r1% (deleted)'), result.group_name)) : result.group_name}</div>`
      },
      {
        label: ctx.__('<tt-Hph6AUKb>', 'Total'),
        key: 'total',
        sortable: false,
        order: '',
        classList: 'text-nowrap col-xs-2 col-xl-3 align-items-center justify-content-center',
        headClassList: 'col-xs-2 col-xl-3',
        render: (html, result, index) => html`<div>${result.total + result.lower_tier_total}</div>`
      },
      {
        label: ctx.__('<tt-8QqtyV3u>', 'Available'),
        key: 'quantity',
        sortable: false,
        order: '',
        classList: 'text-nowrap col-xs-3 col-xl-4 align-items-center',
        headClassList: 'col-xs-3 col-xl-4',
        render: (html, result, index) => html`
          <div class="d-flex flex-row justify-content-center">
            <div class="text-right">${result.quantity + result.lower_tier_quantity}</div>
            <div class="text-left pl-2 mt-n1">
              <div class="d-flex align-items-center">
                ${result.quantity > 0 ?
                    html`
                    <auth-context show-on-auth .permissions="${[permissions.LICENSE_TRANSFER]}">
                      <show-modal class="btn btn-link p-0" modal-id="${modals.LICENSE_TAKE}" .modal-data="${result}">
                        <button class="btn btn-link py-0" @click="${(e) => ctx.showModal(e, result, index)}">${until(ctx.__('<tt-fAm+qikc>', 'Take'))}</button>
                      </show-modal>
                    </auth-context>`:
                  ``}
                  ${result.quantity > 0 && result.group_status === 'active' ? html`<span>|</span>` : ``}
                  ${result.group_status === 'active' ?
                    html`<auth-context show-on-auth .permissions="${[permissions.LICENSE_TRANSFER]}">
                      <show-modal class="btn btn-link p-0" modal-id="${modals.LICENSE_DISTRIBUTE}">
                        <button class="btn btn-link py-0" @click="${(e) => ctx.showModal(e, result, index)}">${until(ctx.__('<tt-7SQs2nXL>', 'Give'))}</button>
                      </show-modal>
                    </auth-context>` :
                    ``}
              </div>
            </div>
          </div>
        `
      },
      {
        label: ctx.__('<tt-E8brzufL>', 'Assigned'),
        key: 'assigned_count',
        sortable: false,
        order: '',
        classList: 'text-nowrap pr-4 col-xs-2-5 col-1-5 align-items-center justify-content-end text-right',
        headClassList: 'text-right pr-4 col-xs-2-5 col-1-5',
        render: (html, result, index) => html`<div class="">${result.assigned_count + result.lower_tier_assigned_count}</div>`
      },
      {
        label: ctx.__('<tt-5sc2NwAd>', 'Redeemed'),
        key: 'redeemed_count',
        sortable: false,
        order: '',
        classList: 'col-xs-2-5 col-1-5 align-items-center text-left',
        headClassList: 'text-left col-xs-2-5 col-1-5',
        render: (html, result, index) => html`<div class="bg-light d-inline-block px-3 rounded redeemed-tab">${result.redeemed_count + result.lower_tier_redeemed_count}</div>`
      },
    ]
  };

  return t10s;
}

/**
 * @summary InventoryAllocation
 * @extends {AoflElement}
 */
@customElement('inventory-allocation')
class InventoryAllocation extends i18nMixin(AoflElement) {
  /**
   * Creates an instance of InventoryAllocation.
   */
  constructor() {
    super();
    this.translations = translations;
    this.storeInstance = storeInstance;
  }

  @property({type: Array})
  tableConfig = [];

  @property({type: Array, mapState: `${sdoNs.LICENSE_INVENTORY_ALLOCATION}.licenseAllocations`, store: storeInstance})
  licenseAllocations = [];

  @property({type: Array, mapState: `${sdoNs.LICENSE_INVENTORY_ALLOCATION}.allocationDrilldown`, store: storeInstance})
  allocationDrilldown = [];

  @property({type: Object, mapState: `${sdoNs.LICENSE_INVENTORY_ALLOCATION}.activeRow`, store: storeInstance})
  activeRow = {};

  @property({type: Boolean, mapState: `${sdoNs.LICENSE_INVENTORY_ALLOCATION}.hasMore`, store: storeInstance})
  hasMore = false;

  @property({type: Object, mapState: `${sdoNs.LICENSE_INVENTORY_ALLOCATION}.queryObj`, store: storeInstance})
  queryObj = {};

  @property({type: Number, mapState: `${sdoNs.LICENSE_INVENTORY_ALLOCATION}.level`, store: storeInstance})
  level = 0;

  @property({type: Boolean, mapState: `${sdoNs.LICENSE_INVENTORY_ALLOCATION}.loading`, store: storeInstance})
  loading = true;

  @property({type: Object, mapState: `${sdoNs.SESSION}.current_role`, store: storeInstance})
  currentRole = {};

  @property({type: Object, mapState: `${sdoNs.LICENSE_INVENTORY_ALLOCATION}.currentGroupLicenseCount`, store: storeInstance})
  currentGroupLicenseCount = {};

  @property({type: Object, mapState: `${sdoNs.LICENSE_INVENTORY_ALLOCATION}.childGroupLicenseCount`, store: storeInstance})
  childGroupLicenseCount = {};

  @property({type: Array, mapState: `${sdoNs.SESSION}.settings.tier_structure.labels`, store: storeInstance})
  tierLabels = [];

  @property({type: String, mapState: `${sdoNs.SESSION}.group_info`, store: storeInstance})
  groupInfo = '';

  @property({type: Number})
  licenseId = parseInt(routerInstance.currentRoute.matchedRoute.props.id, 10);

  connectedCallback() {
    super.connectedCallback();

    const searchParams = SearchParams.decode(routerInstance.currentRoute.url.searchParams);
    defaults(searchParams, DEFAULT_QUERY_OBJ);

    var t10s = setTranslations(this);
    for (let index in t10s) {
      this[index] = t10s[index];
    }

    // set table config sort fields
    for (let i = 0; i < this.tableConfig.length; i++) {
      const item = this.tableConfig[i];
      let itemKey = item.key;
      if (itemKey === 'license_name') {
        itemKey = 'name';
      }
      if (itemKey === 'license_type') {
        itemKey = 'type';
      }
      if (searchParams.sort.fields.indexOf(itemKey) > -1) {
        item.order = searchParams.sort.direction;
        break;
      }
    }

    this.storeInstance.commit(
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setTableConfig', payload: this.tableConfig},
    );
  }

  async onSort(event) {
    this.storeInstance.commit({
      namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION,
      mutationId: 'updateSortOrder',
      payload: {
        index: event.detail.index,
        field: event.detail.item.key
      }
    });
    this.tableConfig.map((item) => {
      if (this.queryObj.sort.fields.indexOf(item.key) > -1) {
        item.order = this.queryObj.sort.direction;
      } else {
        item.order = ''
      }
    });
    const params = Object.assign({}, DEFAULT_SEARCH, this.queryObj.search);
    const resp = await ApiService.searchLicenseAllocations(params, {
      group_parent_id: this.activeRow.group_id,
      license_id: this.licenseId,
    }, this.queryObj.sort);
    this.storeInstance.commit(
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setLicenseAllocations', payload: resp.results},
    );
  }

  async onSelect(event) {
    // Instead of loading information about this row, i want to give licenses
    // so i check to see if the element type is a button, and if it is, return
    if (event.detail.e?.path?.[0]?.nodeName === 'BUTTON') {
      return;
    }
    this.storeInstance.commit({namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setLoading', payload: true});
    const rowInfo = event.detail.data;
    if (rowInfo.group_type === 'classroom' || this.activeRow.group_id === rowInfo.group_id) {
    this.storeInstance.commit({namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setLoading', payload: false});
      return;
    }
    this.storeInstance.commit({namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setActiveRow', payload: rowInfo});
    const params = Object.assign({}, DEFAULT_SEARCH, {query: ''});
    const groupId = rowInfo.group_id;
    const resp = await ApiService.searchLicenseAllocations(params, {
      group_parent_id: groupId,
      license_id: this.licenseId,
    }, DEFAULT_SORT);
    const licenseCounts = await ApiService.groupLicenseGetGroupAndChildLicenseCounts(this.licenseId, groupId);
    this.storeInstance.commit(
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setLoading', payload: false},
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setLicenseAllocations', payload: resp.results},
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setHasMore', payload: resp.has_more},
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'addToAllocationDrilldown', payload: rowInfo},
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setLevel', payload: this.level + 1},
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setCurrentGroupLicenseCount', payload: licenseCounts.current_group_license_count},
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setChildGroupLicenseCount', payload: licenseCounts.child_group_license_count},
    );
  }

  async navThroughBreadcrumbs(rowInfo, level) {
    if (rowInfo.group_id === this.currentRole.group_id) {
      rowInfo = Object.assign(rowInfo, {
        license_id: this.licenseId
      }, this.currentGroupLicenseCount);
    }
    const params = Object.assign({}, DEFAULT_SEARCH, {query: ''});
    const resp = await ApiService.searchLicenseAllocations(params, {
      group_parent_id: rowInfo.group_id,
      license_id: this.licenseId,
    }, DEFAULT_SORT);
    const licenseCounts = await ApiService.groupLicenseGetGroupAndChildLicenseCounts(this.licenseId, rowInfo.group_id);
    this.storeInstance.commit(
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setActiveRow', payload: rowInfo},
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setLicenseAllocations', payload: resp.results},
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'sliceAllocationDrilldown', payload: level},
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setLevel', payload: level},
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setCurrentGroupLicenseCount', payload: licenseCounts.current_group_license_count},
      {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setChildGroupLicenseCount', payload: licenseCounts.child_group_license_count},
    );
  }

  async search() {
    this.storeInstance.commit({namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setLoading', payload: true});
    const params = Object.assign({}, DEFAULT_SEARCH, this.queryObj.search);
    const sortParams = Object.assign({}, DEFAULT_SORT, this.queryObj.sort);
    const resp = await ApiService.searchLicenseAllocations(params, {
      group_parent_id: this.activeRow.group_id,
      license_id: parseInt(routerInstance.currentRoute.matchedRoute.props.id, 10),
    }, sortParams);
    this.storeInstance.commit(
        {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setHasMore', payload: resp.has_more},
        {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setLicenseAllocations', payload: resp.results},
        {namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setLoading', payload: false}
    );
  }

  showModal(e, result, index) {
    const groupPath = this.groupInfo.path.split('.');
    groupPath.pop();

    const groupIds = [...groupPath];
    this.allocationDrilldown.map((item) => {
      groupIds.push(item.group_id);
    });

    groupIds.push(result.group_id);

    this.storeInstance.commit({namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'setSelectedGroups', payload: groupIds});
  }

  disconnectedCallback() {
    this.storeInstance.commit({namespace: sdoNs.LICENSE_INVENTORY_ALLOCATION, mutationId: 'init'});
    super.disconnectedCallback();
  }

  /**
   * @readonly
   */
  static is = 'inventory-allocation';
  /**
   *
   * @return {Object}
   */
  render() {
    return super.render({
      default: {
        template,
        styles: [styles]
      }
    });
  }
}

export default InventoryAllocation;
